import { Link as CarbonLink } from '@carbon/react';
import { ListCardProps } from '@/app/types';
import DynamicTag from '@/components/Tag/tag';
import { validateBackslash } from '@/utils/urls';
import styles from './listCardComponent.module.scss';
import config from '@/../config';

interface ExtendedListCardProps extends ListCardProps {
  index?: number;
  isAboveFold?: boolean;
}

const ListCardComponent: React.FC<ExtendedListCardProps> = ({
  date,
  contentType,
  title,
  excerpt,
  subtitle,
  link,
  imageSrc,
  primaryCategory,
  taxonomies,
  subtype,
  course_portal,
  content_type,
  externalLink,
  index = 0,
  isAboveFold = false,
  displayUrl,
  info,
}) => {
  function getContentTypeTag() {
    const type = content_type?.toLowerCase();
    if (type === 'blogs') return 'Blog post';
    if (type === 'series') return 'Series';
    if (type === 'openprojects') return 'Open Project';
    if (type === 'learningpaths' || type === 'learningpath_items') return 'Learning Path';
    const singular = type ? (type.endsWith('s') ? type.slice(0, -1) : type) : '';
    return singular?.charAt(0).toUpperCase() + singular?.slice(1);
  }

  const finalLink = externalLink || link;
  const isExternal = !!externalLink;

  // Ensure URL ends with '/' for SEO consistency using validateBackslash
  const normalizedLink = validateBackslash(finalLink);

  // Determine rel attribute based on link type
  const relAttribute = isExternal ? 'noopener noreferrer nofollow' : 'noopener';

  // Create descriptive aria-label for better accessibility and SEO
  const ariaLabel = `${title}${date ? ` - ${date}` : ''}${contentType ? ` - ${getContentTypeTag()}` : ''}`;

  // Only prioritize first 2-3 images for better performance
  const shouldPrioritizeImage = isAboveFold || index < 3;

  return (
    <article className={styles.resultItem}>
      <CarbonLink
        className={styles.contentLink}
        href={normalizedLink}
        rel={relAttribute}
        aria-label={ariaLabel}
      >
        <div className={styles.contentContainer}>
          {(date || contentType) && info !== false && (
            <div className={styles.info}>
              <span>
                {content_type !== 'courses' ? date && <p className={styles.date}>{date}</p> : null}
                {contentType && (
                  <p className={styles.type}>
                    <span> {getContentTypeTag()}</span>
                  </p>
                )}
              </span>

              {content_type === 'courses' ? (
                <p className={styles.additional_info}>
                  <span>
                    {subtype !== 'default' && subtype !== 'Course' && <>{subtype}</>}
                    {course_portal && (
                      <>
                        {subtype !== 'default' && subtype !== 'Course' ? ' | ' : ''}
                        {course_portal}
                      </>
                    )}
                  </span>
                </p>
              ) : null}
            </div>
          )}

          <div className={styles.detail}>
            <h3 className={styles.title}>{title}</h3>
            {displayUrl ? (
              <p className={styles.displayUrl}>{config.thisHost + displayUrl}</p>
            ) : null}
            <p className={styles.excerpt}>
              {content_type?.toLowerCase() === 'blogs' ? subtitle : excerpt}
            </p>

            {primaryCategory && contentType && (
              <DynamicTag
                categories={primaryCategory}
                contentType={contentType}
                type="gray"
                className={styles.customTag}
                taxonomies={taxonomies || {}}
                disableLinks={true}
              />
            )}
          </div>
        </div>
      </CarbonLink>
      <hr />
    </article>
  );
};

export default ListCardComponent;
